import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useAuth } from "../context/AuthContext";
import type { InvitePrefill } from "../domain/users/usersHandover";
import { api } from "../services/api";
import {
  Paper,
  Typography,
  TextField,
  Alert,
  Box,
  Tabs,
  Tab,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import PageContainer from "./PageContainer";
import {
  NoticeboardButton,
  ActionModal,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
  TabPanel,
} from "./shared";
import { useCircles } from "../hooks/useCircles";
import { getAvatarColor, getInitials } from "../utils/avatarUtils";
import { lightTheme } from "../styles/theme";

const inviteSettingsSectionSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
} as const;

const inviteSectionTitleSx = {
  fontWeight: 600,
  color: "text.secondary",
  letterSpacing: "0.02em",
  fontSize: { xs: "0.9375rem", sm: "0.875rem" },
} as const;

const inviteSubsectionTitleSx = {
  fontWeight: 600,
  fontSize: "0.8125rem",
  color: "text.primary",
  letterSpacing: "0.01em",
} as const;

function InviteSubsectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="h3" variant="body2" sx={inviteSubsectionTitleSx}>
      {children}
    </Typography>
  );
}

const inviteSectionHelperSx = {
  fontSize: "0.8125rem",
  lineHeight: 1.45,
} as const;

const inviteTabSx = {
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.8125rem",
  minHeight: 44,
  px: { xs: 1, sm: 2 },
} as const;

const inviteRadioOptionTitleSx = {
  fontWeight: 500,
  fontSize: "0.8125rem",
  display: "block",
  lineHeight: 1.35,
} as const;

const inviteRadioOptionDescSx = {
  fontSize: "0.75rem",
  color: "text.secondary",
  display: "block",
  mt: 0.25,
  lineHeight: 1.4,
} as const;

function InviteSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle2" sx={inviteSectionTitleSx}>
      {children}
    </Typography>
  );
}

export type InviteUsersInitialInvite = InvitePrefill;

export interface InviteUsersActionModalProps {
  open: boolean;
  onClose: () => void;
}

interface InviteUsersProps {
  hideHeader?: boolean;
  onSuccess?: (message?: string) => void;
  hideButton?: boolean;
  initialInvite?: InviteUsersInitialInvite;
  /** When set, wraps the flow in ActionModal with Send Invitations pinned to the bottom on mobile. */
  actionModalProps?: InviteUsersActionModalProps;
}

const INVITE_USERS_FORM_ID = "invite-users-form";

const getCircleLogoSrc = (logo?: string): string | undefined =>
  logo && logo.trim() !== "" && !logo.includes("placehold.co") ? logo : undefined;

function uniqueStrings(values: string[]): string[] {
  return values.filter((value, index) => values.indexOf(value) === index);
}

function parseInviteEntries(raw: string): string[] {
  return uniqueStrings(
    raw
      .split(/[\n,;]+/)
      .map((entry) => entry.trim())
      .filter(Boolean)
  );
}

export interface InviteEntriesFieldHandle {
  flush: () => string[];
}

interface InviteEntriesFieldProps {
  entries: string[];
  onChange: (entries: string[]) => void;
  placeholder: string;
  addPlaceholder: string;
  entryLabel: string;
  normalizeEntry?: (entry: string) => string;
  fieldSx?: object;
}

const InviteEntriesField = forwardRef<
  InviteEntriesFieldHandle,
  InviteEntriesFieldProps
>(function InviteEntriesField(
  {
    entries,
    onChange,
    placeholder,
    addPlaceholder,
    entryLabel,
    normalizeEntry,
    fieldSx,
  },
  ref
) {
  const [draft, setDraft] = useState("");

  const mergeEntries = useCallback(
    (raw: string, base: string[]) => {
      const parsed = parseInviteEntries(raw);
      if (parsed.length === 0) {
        return base;
      }
      const normalized = normalizeEntry
        ? parsed.map(normalizeEntry)
        : parsed;
      return uniqueStrings([...base, ...normalized]);
    },
    [normalizeEntry]
  );

  useImperativeHandle(
    ref,
    () => ({
      flush: () => {
        if (!draft.trim()) {
          return entries;
        }
        const merged = mergeEntries(draft, entries);
        onChange(merged);
        setDraft("");
        return merged;
      },
    }),
    [draft, entries, mergeEntries, onChange]
  );

  const addFromDraft = () => {
    if (!draft.trim()) {
      return;
    }
    onChange(mergeEntries(draft, entries));
    setDraft("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addFromDraft();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const text = event.clipboardData.getData("text");
    if (/[\n,;]/.test(text)) {
      event.preventDefault();
      onChange(mergeEntries(text, entries));
      setDraft("");
    }
  };

  const removeEntry = (entry: string) => {
    onChange(entries.filter((value) => value !== entry));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={inviteSectionHelperSx}
        >
          {entries.length === 0
            ? placeholder
            : `${entries.length} ${entryLabel}${entries.length === 1 ? "" : "s"}`}
        </Typography>
        {entries.length > 0 && (
          <Button
            size="small"
            color="inherit"
            onClick={() => onChange([])}
            sx={{
              minWidth: 0,
              fontSize: "0.75rem",
              py: 0.25,
              textTransform: "none",
              color: "text.secondary",
            }}
          >
            Clear all
          </Button>
        )}
      </Box>

      {entries.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            maxHeight: { xs: 160, md: 200 },
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {entries.map((entry) => (
            <Chip
              key={entry}
              label={entry}
              size="small"
              onDelete={() => removeEntry(entry)}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: "auto",
                maxWidth: "100%",
                fontWeight: 500,
                "& .MuiChip-label": {
                  display: "block",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  py: 0.5,
                  lineHeight: 1.35,
                  fontSize: "0.8125rem",
                },
              }}
            />
          ))}
        </Box>
      )}

      <TextField
        size="small"
        fullWidth
        placeholder={entries.length > 0 ? addPlaceholder : undefined}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={addFromDraft}
        sx={fieldSx}
      />
    </Box>
  );
});

const InviteUsers: React.FC<InviteUsersProps> = ({
  hideHeader = false,
  onSuccess,
  hideButton = false,
  initialInvite,
  actionModalProps,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, token } = useAuth();
  const { data: circles = [], isLoading: circlesLoading } = useCircles();
  const [tabValue, setTabValue] = useState(0);
  const [emailEntries, setEmailEntries] = useState<string[]>([]);
  const [phoneEntries, setPhoneEntries] = useState<string[]>([]);
  const [usernameEntries, setUsernameEntries] = useState<string[]>([]);
  const emailFieldRef = useRef<InviteEntriesFieldHandle>(null);
  const phoneFieldRef = useRef<InviteEntriesFieldHandle>(null);
  const usernameFieldRef = useRef<InviteEntriesFieldHandle>(null);
  const [selectedCircleIds, setSelectedCircleIds] = useState<string[]>([]);
  const [circleMemberAlias, setCircleMemberAlias] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [legacyAccessOption, setLegacyAccessOption] = useState<
    "none" | "7days" | "all"
  >("7days"); // Default to 7 days

  const availableCircles = useMemo(() => {
    const sub = circles.filter((circle) => circle.circleId !== user?.schoolId);
    if (user?.userType !== "team") {
      return sub;
    }
    const allowed = user?.circleIds;
    if (!Array.isArray(allowed) || allowed.length === 0) {
      return [];
    }
    const allow = new Set(allowed.map(String));
    return sub.filter((c) => allow.has(c.circleId));
  }, [circles, user?.schoolId, user?.userType, user?.circleIds]);

  const activeContactCount = useMemo(() => {
    if (tabValue === 0) return emailEntries.filter(Boolean).length;
    if (tabValue === 1) return phoneEntries.filter(Boolean).length;
    return usernameEntries.filter(Boolean).length;
  }, [tabValue, emailEntries, phoneEntries, usernameEntries]);

  const contactTypeLabel = useMemo(() => {
    if (tabValue === 1) return "phone number";
    if (tabValue === 2) return "username";
    return "email";
  }, [tabValue]);

  const selectedCircleLabels = useMemo(
    () =>
      selectedCircleIds
        .map(
          (circleId) =>
            availableCircles.find((circle) => circle.circleId === circleId)
              ?.name,
        )
        .filter((name): name is string => Boolean(name)),
    [selectedCircleIds, availableCircles],
  );

  const addUserButtonLabel = useMemo(() => {
    if (activeContactCount > 1) {
      return `Add ${activeContactCount} users`;
    }
    return "Add user";
  }, [activeContactCount]);

  const footerSummary = useMemo(() => {
    const contactSummary =
      activeContactCount > 0
        ? `${activeContactCount} ${contactTypeLabel}${
            activeContactCount > 1 ? "s" : ""
          } ready`
        : `Add a ${contactTypeLabel} on the left`;

    if (selectedCircleLabels.length === 0) {
      return contactSummary;
    }

    return `${contactSummary} · ${selectedCircleLabels.join(", ")}`;
  }, [
    activeContactCount,
    contactTypeLabel,
    selectedCircleLabels,
  ]);

  const mobileFooterSummary = useMemo(() => {
    if (activeContactCount > 0) {
      return `${activeContactCount} ready`;
    }
    return `Add a ${contactTypeLabel} to continue`;
  }, [activeContactCount, contactTypeLabel]);

  const canSubmitInvite = activeContactCount > 0;

  const toggleCircleSelection = useCallback((circleId: string) => {
    setSelectedCircleIds((prev) =>
      prev.includes(circleId)
        ? prev.filter((id) => id !== circleId)
        : [...prev, circleId],
    );
  }, []);

  const legacyAccessAll = legacyAccessOption === "all";
  const legacyAccessDays =
    legacyAccessOption === "7days" ? 7 : undefined;

  const modalFieldSx = useMemo(() => {
    const ff = theme.typography.fontFamily;
    const fontFamily =
      ff == null ? "inherit" : Array.isArray(ff) ? ff.join(", ") : ff;
    return {
      fontFamily,
      "& .MuiOutlinedInput-root": {
        minHeight: 40,
        borderRadius: `${theme.shape.borderRadius}px`,
      },
    };
  }, [theme]);

  useEffect(() => {
    if (!actionModalProps?.open) return;
    const email = initialInvite?.email?.trim();
    const phone = initialInvite?.phoneNumber?.trim();
    setEmailEntries(email ? [email] : []);
    setPhoneEntries(phone ? [phone] : []);
    setUsernameEntries([]);
    setSelectedCircleIds(initialInvite?.circleIds ?? []);
    setCircleMemberAlias(initialInvite?.alias?.trim() ?? "");
    setError(null);
    setSuccess(null);
    setTabValue(email ? 0 : phone ? 1 : 0);
    setLegacyAccessOption("7days");
  }, [actionModalProps?.open, initialInvite]);

  const addContactToCircles = useCallback(
    async (contact: {
      email?: string;
      phoneNumber?: string;
      username?: string;
    }): Promise<{ succeeded: string[]; failed: string[] }> => {
      if (
        selectedCircleIds.length === 0 ||
        !user?.schoolId ||
        !token
      ) {
        return { succeeded: [], failed: [] };
      }

      const aliasTrimmed = circleMemberAlias.trim();
      const alias =
        aliasTrimmed.length > 0 && aliasTrimmed.length <= 60
          ? aliasTrimmed
          : undefined;

      const results = await Promise.allSettled(
        selectedCircleIds.map((circleId) =>
          api.circles.addUserToSubCircle(
            {
              ...contact,
              subcircleId: circleId,
              schoolId: user.schoolId as string,
              accessLegacyMessages: legacyAccessAll,
              legacyAccessDays,
              alias,
            },
            token
          )
        )
      );

      const succeeded: string[] = [];
      const failed: string[] = [];

      results.forEach((result, index) => {
        const circleId = selectedCircleIds[index];
        const circleName =
          availableCircles.find((c) => c.circleId === circleId)?.name ||
          circleId;
        if (result.status === "fulfilled") {
          succeeded.push(circleName);
          return;
        }
        const msg =
          (result.reason as Error)?.message || "Unknown error";
        if (/already.*subcircle|already a member/i.test(msg)) {
          succeeded.push(circleName);
        } else {
          failed.push(`${circleName} (${msg})`);
        }
      });

      return { succeeded, failed };
    },
    [
      selectedCircleIds,
      user?.schoolId,
      token,
      legacyAccessAll,
      legacyAccessDays,
      availableCircles,
      circleMemberAlias,
    ]
  );

  const appendCircleAssignmentMessages = (
    messages: string[],
    circleResults: { succeeded: string[]; failed: string[] }
  ) => {
    const uniqueCircles = uniqueStrings(circleResults.succeeded);
    if (uniqueCircles.length > 0) {
      messages.push(
        `✓ Added to circle${uniqueCircles.length > 1 ? "s" : ""}: ${uniqueCircles.join(", ")}`
      );
    }
    if (circleResults.failed.length > 0) {
      messages.push(
        `⚠ Could not add to some circles: ${circleResults.failed.join(", ")}`
      );
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Basic phone validation - at least 10 digits, can include +, spaces, dashes, parentheses
    const cleaned = phone.replace(/[\s\-()]/g, "");
    return /^[+]?[0-9]{10,}$/.test(cleaned);
  };

  const validateUsername = (username: string): boolean => {
    // Username validation: alphanumeric, 3-20 characters, can include underscores and hyphens
    return /^[a-zA-Z0-9_-]{3,20}$/.test(username.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    if (!user?.schoolId || !token) {
      setError("You must be logged in to invite users");
      setIsLoading(false);
      return;
    }

    const aliasTrimmed = circleMemberAlias.trim();
    if (aliasTrimmed.length > 60) {
      setError("Display alias must be 60 characters or less.");
      setIsLoading(false);
      return;
    }

    let successMessageToPass: string | undefined = undefined;

    try {
      if (tabValue === 0) {
        // Email invitations
        const emailList = emailFieldRef.current?.flush() ?? emailEntries;
        const validEmails = emailList.filter(
          (email) => email && validateEmail(email)
        );
        const invalidEmails = emailList.filter(
          (email) => email && !validateEmail(email)
        );

        if (invalidEmails.length > 0) {
          setError(
            `Invalid email format for: ${invalidEmails.join(
              ", "
            )}. Please enter valid email addresses.`
          );
          setIsLoading(false);
          return;
        }

        if (validEmails.length === 0) {
          setError("Please enter at least one valid email address");
          setIsLoading(false);
          return;
        }

        const results = await Promise.allSettled(
          validEmails.map(async (email) => {
            const response = await api.auth.linkUserToSchool(
              user.schoolId as string,
              email,
              token,
              legacyAccessOption === "all",
              legacyAccessOption === "7days" ? 7 : undefined,
              aliasTrimmed || undefined
            );
            return { email, response };
          })
        );

        const successfulEmails: string[] = [];
        const invitedEmails: string[] = [];
        const linkedEmails: string[] = [];

        const circleAssignmentNotes: string[] = [];

        for (const result of results) {
          if (result.status === "fulfilled") {
            const { email, response } = result.value;
            successfulEmails.push(email);
            if (response.status === "pending" || response.invitationId) {
              invitedEmails.push(email);
            } else {
              linkedEmails.push(email);
            }
            const circleResults = await addContactToCircles({ email });
            appendCircleAssignmentMessages(circleAssignmentNotes, circleResults);
          }
        }

        const failedEmails = results
          .filter((result) => result.status === "rejected")
          .map((result, index) => {
            const rejection = result as PromiseRejectedResult;
            const originalIndex = results.findIndex((r) => r === result);
            const email = validEmails[originalIndex];
            const errorMessage = rejection.reason?.message || "Unknown error";
            return `${email} (${errorMessage})`;
          });

        if (successfulEmails.length > 0) {
          const messages: string[] = [];
          if (linkedEmails.length > 0) {
            messages.push(
              `✓ Added ${linkedEmails.length} user${
                linkedEmails.length > 1 ? "s" : ""
              }: ${linkedEmails.join(", ")}`
            );
          }
          if (invitedEmails.length > 0) {
            messages.push(
              `✓ Invitation${invitedEmails.length > 1 ? "s" : ""} sent to ${
                invitedEmails.length
              } user${
                invitedEmails.length > 1 ? "s" : ""
              }: ${invitedEmails.join(
                ", "
              )}. They'll be automatically added when they sign up.`
            );
          }
          const uniqueCircleNotes = uniqueStrings(circleAssignmentNotes);
          if (uniqueCircleNotes.length > 0) {
            messages.push(...uniqueCircleNotes);
          }
          const successMsg = messages.join("\n");
          setSuccess(successMsg);
          successMessageToPass = successMsg;
        }

        if (failedEmails.length > 0) {
          setError(`Failed to invite: ${failedEmails.join(", ")}`);
        }

        setEmailEntries([]);
        setSelectedCircleIds([]);
      } else if (tabValue === 1) {
        // Phone number invitations
        const phoneList = phoneFieldRef.current?.flush() ?? phoneEntries;
        const validPhones = phoneList.filter(
          (phone) => phone && validatePhoneNumber(phone)
        );
        const invalidPhones = phoneList.filter(
          (phone) => phone && !validatePhoneNumber(phone)
        );

        if (invalidPhones.length > 0) {
          setError(
            `Invalid phone format for: ${invalidPhones.join(
              ", "
            )}. Please enter valid phone numbers.`
          );
          setIsLoading(false);
          return;
        }

        if (validPhones.length === 0) {
          setError("Please enter at least one valid phone number");
          setIsLoading(false);
          return;
        }

        const results = await Promise.allSettled(
          validPhones.map(async (phone) => {
            const response = await api.auth.linkUserToSchoolByPhone(
              user.schoolId as string,
              phone,
              token,
              legacyAccessOption === "all",
              legacyAccessOption === "7days" ? 7 : undefined,
              aliasTrimmed || undefined
            );
            return { phone, response };
          })
        );

        const successfulPhones: string[] = [];
        const invitedPhones: string[] = [];
        const linkedPhones: string[] = [];

        const circleAssignmentNotes: string[] = [];

        for (const result of results) {
          if (result.status === "fulfilled") {
            const { phone, response } = result.value;
            successfulPhones.push(phone);
            if (response.status === "pending" || response.invitationId) {
              invitedPhones.push(phone);
            } else {
              linkedPhones.push(phone);
            }
            const circleResults = await addContactToCircles({
              phoneNumber: phone,
            });
            appendCircleAssignmentMessages(circleAssignmentNotes, circleResults);
          }
        }

        const failedPhones = results
          .filter((result) => result.status === "rejected")
          .map((result, index) => {
            const rejection = result as PromiseRejectedResult;
            const originalIndex = results.findIndex((r) => r === result);
            const phone = validPhones[originalIndex];
            const errorMessage = rejection.reason?.message || "Unknown error";
            return `${phone} (${errorMessage})`;
          });

        if (successfulPhones.length > 0) {
          const messages: string[] = [];
          if (linkedPhones.length > 0) {
            messages.push(
              `✓ Added ${linkedPhones.length} user${
                linkedPhones.length > 1 ? "s" : ""
              }: ${linkedPhones.join(", ")}`
            );
          }
          if (invitedPhones.length > 0) {
            messages.push(
              `✓ Invitation${invitedPhones.length > 1 ? "s" : ""} sent to ${
                invitedPhones.length
              } user${
                invitedPhones.length > 1 ? "s" : ""
              }: ${invitedPhones.join(
                ", "
              )}. They'll be automatically added when they sign up.`
            );
          }
          const uniqueCircleNotes = uniqueStrings(circleAssignmentNotes);
          if (uniqueCircleNotes.length > 0) {
            messages.push(...uniqueCircleNotes);
          }
          const successMsg = messages.join("\n");
          setSuccess(successMsg);
          successMessageToPass = successMsg;
        }

        if (failedPhones.length > 0) {
          setError(`Failed to invite: ${failedPhones.join(", ")}`);
        }

        setPhoneEntries([]);
        setSelectedCircleIds([]);
      } else if (tabValue === 2) {
        // Username invitations
        const usernameList =
          usernameFieldRef.current?.flush() ?? usernameEntries;
        const validUsernames = usernameList.filter(
          (username) => username && validateUsername(username)
        );
        const invalidUsernames = usernameList.filter(
          (username) => username && !validateUsername(username)
        );

        if (invalidUsernames.length > 0) {
          setError(
            `Invalid username format for: ${invalidUsernames.join(
              ", "
            )}. Usernames must be 3-20 characters, alphanumeric with underscores and hyphens only.`
          );
          setIsLoading(false);
          return;
        }

        if (validUsernames.length === 0) {
          setError("Please enter at least one valid username");
          setIsLoading(false);
          return;
        }

        const results = await Promise.allSettled(
          validUsernames.map(async (username) => {
            await api.auth.linkUserToSchoolByUsername(
              user.schoolId as string,
              username,
              token
            );
            return username;
          })
        );

        const circleAssignmentNotes: string[] = [];

        for (const result of results) {
          if (result.status === "fulfilled") {
            const username = result.value;
            const circleResults = await addContactToCircles({ username });
            appendCircleAssignmentMessages(circleAssignmentNotes, circleResults);
          }
        }

        const successfulUsernames = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => (result as PromiseFulfilledResult<string>).value);

        const failedUsernames = results
          .filter((result) => result.status === "rejected")
          .map((result, index) => {
            const rejection = result as PromiseRejectedResult;
            const username = validUsernames[index];
            const errorMessage = rejection.reason?.message || "Unknown error";
            return `${username} (${errorMessage})`;
          });

        if (successfulUsernames.length > 0) {
          const messages = [
            `Successfully invited: ${successfulUsernames.join(", ")}`,
          ];
          const uniqueCircleNotes = uniqueStrings(circleAssignmentNotes);
          if (uniqueCircleNotes.length > 0) {
            messages.push(...uniqueCircleNotes);
          }
          const successMsg = messages.join("\n");
          setSuccess(successMsg);
          successMessageToPass = successMsg;
        }

        if (failedUsernames.length > 0) {
          setError(`Failed to invite: ${failedUsernames.join(", ")}`);
        }

        setUsernameEntries([]);
        setSelectedCircleIds([]);
      }

      // Call onSuccess callback immediately with success message
      if (onSuccess && successMessageToPass) {
        onSuccess(successMessageToPass);
      }
    } catch (err) {
      setError("Failed to invite users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const showInvitePageTitle = !hideHeader && !actionModalProps;

  const content = (
    <Box
      component="form"
      id={actionModalProps ? INVITE_USERS_FORM_ID : undefined}
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: actionModalProps ? 2 : 3,
      }}
    >
      {showInvitePageTitle && (
        <>
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, color: "primary.main" }}
          >
            Invite Users
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Add users to your organization by email or phone number. Users
            without accounts will be automatically added when they sign up with
            the same email or phone.
          </Typography>
        </>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2.5, md: 3 },
          pt: actionModalProps ? { xs: 0.5, sm: 1 } : 0,
          alignItems: { md: "flex-start" },
        }}
      >
        {/* Left column — contacts */}
        <Box sx={{ flex: 1.2, minWidth: 0, width: "100%" }}>
          {actionModalProps && (
            <Box sx={{ mb: 1.5 }}>
              <InviteSectionTitle>User details</InviteSectionTitle>
            </Box>
          )}

          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 0.5,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="invite method tabs"
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                minHeight: 44,
                "& .MuiTabs-indicator": { height: 2 },
              }}
            >
              <Tab
                label="Email"
                id="invite-tab-0"
                aria-controls="invite-tabpanel-0"
                sx={inviteTabSx}
              />
              <Tab
                label="Phone"
                id="invite-tab-1"
                aria-controls="invite-tabpanel-1"
                sx={inviteTabSx}
              />
              <Tab
                label="Username"
                id="invite-tab-2"
                aria-controls="invite-tabpanel-2"
                sx={inviteTabSx}
              />
            </Tabs>
          </Box>

          <Box sx={{ minHeight: { md: 300 } }}>
            <TabPanel idPrefix="invite" value={tabValue} index={0} contentSx={{ pt: 1.5 }}>
              <InviteEntriesField
                ref={emailFieldRef}
                entries={emailEntries}
                onChange={setEmailEntries}
                entryLabel="email address"
                placeholder="Paste or type emails (comma or new line separated)"
                addPlaceholder="Add another email, then press Enter"
                normalizeEntry={(email) => email.toLowerCase()}
                fieldSx={modalFieldSx}
              />
            </TabPanel>

            <TabPanel idPrefix="invite" value={tabValue} index={1} contentSx={{ pt: 1.5 }}>
              <InviteEntriesField
                ref={phoneFieldRef}
                entries={phoneEntries}
                onChange={setPhoneEntries}
                entryLabel="phone number"
                placeholder="Paste or type phone numbers (comma or new line separated)"
                addPlaceholder="Add another phone number, then press Enter"
                fieldSx={modalFieldSx}
              />
            </TabPanel>

            <TabPanel idPrefix="invite" value={tabValue} index={2} contentSx={{ pt: 1.5 }}>
              <InviteEntriesField
                ref={usernameFieldRef}
                entries={usernameEntries}
                onChange={setUsernameEntries}
                entryLabel="username"
                placeholder="Paste or type usernames (e.g. john_doe, jane-smith)"
                addPlaceholder="Add another username, then press Enter"
                fieldSx={modalFieldSx}
              />
            </TabPanel>
          </Box>
        </Box>

        {/* Right column — circles & historical access */}
        <Box
          sx={{
            flex: 0.95,
            minWidth: 0,
            width: "100%",
            pl: { md: 2.5 },
            borderLeft: { md: "1px solid" },
            borderColor: { md: "divider" },
            position: { md: "sticky" },
            top: { md: 8 },
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: { xs: 2, md: 0 },
            borderTop: { xs: "1px solid", md: "none" },
          }}
        >
          {actionModalProps && (
            <InviteSectionTitle>Invitation settings</InviteSectionTitle>
          )}

          <Box sx={inviteSettingsSectionSx}>
            <InviteSubsectionTitle>Add to circles (optional)</InviteSubsectionTitle>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={inviteSectionHelperSx}
            >
              Choose one or more circles below, then click{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                Add user
              </Box>{" "}
              at the bottom of this window.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(3, minmax(0, 1fr))",
                  sm: "repeat(4, minmax(0, 1fr))",
                },
                gap: { xs: 0.75, sm: 0.9 },
              }}
            >
              {circlesLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 0.5, py: 1, gridColumn: "1 / -1" }}
                >
                  Loading circles...
                </Typography>
              ) : availableCircles.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 0.5, py: 1, gridColumn: "1 / -1" }}
                >
                  No circles available
                </Typography>
              ) : (
                availableCircles.map((circle) => {
                  const isSelected = selectedCircleIds.includes(circle.circleId);
                  const logoSrc = getCircleLogoSrc(circle.logo);
                  return (
                    <Paper
                      key={circle.circleId}
                      elevation={0}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onClick={() => toggleCircleSelection(circle.circleId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleCircleSelection(circle.circleId);
                        }
                      }}
                      sx={{
                        position: "relative",
                        border: "1px solid",
                        borderColor: isSelected ? "primary.main" : "divider",
                        borderRadius: 1.5,
                        p: { xs: 0.65, sm: 0.75 },
                        cursor: "pointer",
                        bgcolor: isSelected
                          ? alpha(theme.palette.primary.main, 0.06)
                          : "background.paper",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {isSelected ? (
                        <CheckCircleIcon
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            fontSize: 15,
                            color: "primary.main",
                          }}
                        />
                      ) : null}
                      <Box
                        sx={{
                          width: { xs: 34, sm: 36 },
                          height: { xs: 34, sm: 36 },
                          borderRadius: "50%",
                          mx: "auto",
                          mb: 0.5,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: logoSrc
                            ? "background.paper"
                            : getAvatarColor(circle.name, lightTheme),
                          color: "common.white",
                        }}
                      >
                        {logoSrc ? (
                          <Box
                            component="img"
                            src={logoSrc}
                            alt=""
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                          >
                            {getInitials(circle.name)}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          fontSize: "0.72rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {circle.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          textAlign: "center",
                          mt: 0.15,
                          fontSize: "0.62rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {circle.isPublic ? "Public" : "Private"} •{" "}
                        {circle.memberCount} members
                      </Typography>
                    </Paper>
                  );
                })
              )}
            </Box>
            {selectedCircleIds.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {selectedCircleIds.map((circleId) => {
                  const circle = availableCircles.find(
                    (c) => c.circleId === circleId
                  );
                  return (
                    <Chip
                      key={circleId}
                      label={circle?.name || circleId}
                      size="small"
                      onDelete={() =>
                        setSelectedCircleIds((prev) =>
                          prev.filter((id) => id !== circleId)
                        )
                      }
                      sx={{
                        fontWeight: 500,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: "primary.dark",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
                      }}
                    />
                  );
                })}
              </Box>
            )}
            {selectedCircleIds.length > 0 && actionModalProps && (
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                {selectedCircleLabels.length} circle
                {selectedCircleLabels.length > 1 ? "s" : ""} selected. Click{" "}
                <strong>Add user</strong> below to finish.
              </Alert>
            )}
            <TextField
              label="Display alias (optional)"
              placeholder="e.g. John's Mom"
              value={circleMemberAlias}
              onChange={(e) => setCircleMemberAlias(e.target.value)}
              inputProps={{ maxLength: 60 }}
              helperText={
                selectedCircleIds.length > 0
                  ? "Shown on pending invites and in selected circles (max 60 characters)"
                  : "Shown on the pending invitations list so staff can tell who this is (max 60 characters)"
              }
              fullWidth
              sx={modalFieldSx}
            />
          </Box>

          <Box
            sx={{
              ...inviteSettingsSectionSx,
              pt: 2.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <InviteSubsectionTitle>Historical message access</InviteSubsectionTitle>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={legacyAccessOption}
                onChange={(e) =>
                  setLegacyAccessOption(
                    e.target.value as "none" | "7days" | "all"
                  )
                }
                sx={{ gap: 0.5 }}
              >
                <FormControlLabel
                  value="none"
                  control={<Radio color="primary" size="small" />}
                  label={
                    <Box>
                      <Typography sx={inviteRadioOptionTitleSx}>
                        No access to historical messages
                      </Typography>
                      <Typography sx={inviteRadioOptionDescSx}>
                        Users will only see notices created from the day they
                        join onwards.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0.5, alignItems: "flex-start", mx: 0 }}
                />
                <FormControlLabel
                  value="7days"
                  control={<Radio color="primary" size="small" />}
                  label={
                    <Box>
                      <Typography sx={inviteRadioOptionTitleSx}>
                        Allow access to historical messages from last 7 days
                      </Typography>
                      <Typography sx={inviteRadioOptionDescSx}>
                        Users will see notices from the last 7 days before they
                        join, plus all future notices.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0.5, alignItems: "flex-start", mx: 0 }}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio color="primary" size="small" />}
                  label={
                    <Box>
                      <Typography sx={inviteRadioOptionTitleSx}>
                        Allow access to all historical messages
                      </Typography>
                      <Typography sx={inviteRadioOptionDescSx}>
                        Users will see all notices from this organization,
                        including messages from before they joined.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mx: 0 }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && !hideHeader && (
        <Alert
          severity="success"
          sx={{
            whiteSpace: "pre-line", // Allow line breaks
          }}
        >
          {success}
        </Alert>
      )}
      {!hideButton && !actionModalProps && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <NoticeboardButton
            type="submit"
            variant="primary"
            size="medium"
            disabled={isLoading}
            loading={isLoading}
            sx={{
              minWidth: 180,
            }}
          >
            {isLoading ? "Sending..." : addUserButtonLabel}
          </NoticeboardButton>
        </Box>
      )}
    </Box>
  );

  if (actionModalProps) {
    return (
      <ActionModal
        open={actionModalProps.open}
        onClose={actionModalProps.onClose}
        title="Add User"
        subtitle="Step 1: add an email or phone. Step 2: optionally choose circles. Step 3: click Add user at the bottom."
        maxWidth="lg"
        headerStyle="light"
        denseContent
        contentSx={{
          px: { xs: 2, sm: 2.5 },
        }}
        footerSx={{ px: { xs: 2.5 } }}
        disableBackdropClick={isLoading}
        footer={
          !hideButton ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                width: "100%",
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Typography
                variant="body2"
                color={canSubmitInvite ? "text.secondary" : "warning.main"}
                sx={{ fontSize: "0.8125rem", lineHeight: 1.45 }}
              >
                {isMobile ? mobileFooterSummary : footerSummary}
              </Typography>
              <NoticeboardButton
                type="submit"
                form={INVITE_USERS_FORM_ID}
                variant="primary"
                size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
                disabled={isLoading || !canSubmitInvite}
                loading={isLoading}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  borderRadius: 1,
                  flexShrink: 0,
                  "&.Mui-disabled": {
                    bgcolor: alpha(theme.palette.primary.main, 0.35),
                    color: alpha(theme.palette.common.white, 0.92),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                {isLoading ? "Adding..." : addUserButtonLabel}
              </NoticeboardButton>
            </Box>
          ) : null
        }
      >
        {content}
      </ActionModal>
    );
  }

  if (hideHeader) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          height: "100%",
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <PageContainer>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          minHeight: "60vh",
        }}
      >
        {content}
      </Paper>
    </PageContainer>
  );
};

export default InviteUsers;
